import React, { useState, useEffect } from 'react'
import { getPrintAtHomeDocuments, downloadPrintAtHomeDocument } from '../../lib/spektrix'
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import fetcher from '@lib/fetcher'

const PrintHomeDocuments = () => {
  const [documents, setDocuments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadDocuments = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const docs = await getPrintAtHomeDocuments()

        // Fetch event details (including images) for each unique event
        const uniqueEventIds = [...new Set(docs.map(doc => doc.instance?.event?.id).filter(Boolean))]

        // Fetch event data from Rails backend for each unique event
        const eventDataPromises = uniqueEventIds.map(eventId =>
          fetcher(`/events/${eventId}.json`)
            .then(data => ({ id: eventId, data }))
            .catch(err => {
              console.warn(`Failed to fetch event ${eventId}:`, err)
              return { id: eventId, data: null }
            })
        )

        const eventDataResults = await Promise.all(eventDataPromises)
        const eventDataMap = Object.fromEntries(
          eventDataResults.map(({ id, data }) => [id, data])
        )

        // Enhance documents with event data from Rails
        const enhancedDocs = docs.map(doc => ({
          ...doc,
          eventData: eventDataMap[doc.instance?.event?.id] || null
        }))

        console.log('Print-at-home documents:', enhancedDocs)
        setDocuments(enhancedDocs)
      } catch (err) {
        console.error('Failed to load print-at-home documents:', err)
        setError('Failed to load your documents. Please try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    loadDocuments()
  }, [])

  const handleDownload = (documentId) => {
    try {
      downloadPrintAtHomeDocument(documentId)
    } catch (err) {
      console.error('Failed to download document:', err)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London'
    })
  }

  const getEventImageUrl = (doc) => {
    // Prioritize square image for thumbnail display
    console.log('Event data for doc:', doc.id, doc.eventData)
    if (doc.eventData?.square_image_url) {
      console.log('Using square_image_url:', doc.eventData.square_image_url)
      return doc.eventData.square_image_url
    }
    if (doc.eventData?.image_url) {
      console.log('Using image_url:', doc.eventData.image_url)
      return doc.eventData.image_url
    }
    console.log('No image URL found, should show placeholder')
    return null
  }

  const getEventName = (doc) => {
    // Prefer event name from Spektrix expanded data
    if (doc.instance?.event?.name) {
      return doc.instance.event.name
    }
    // Fallback to eventData from Rails
    if (doc.eventData?.name) {
      return doc.eventData.name
    }
    return 'Event Ticket'
  }

  const getEventSlug = (doc) => {
    return doc.eventData?.slug || null
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-white">{error}</span>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-6 text-white">Print at Home Documents</h2>
        <div className="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span className="text-white">You don't have any print-at-home documents available at the moment.</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-white">Print at Home Documents</h2>
      <p className="text-gray-400 mb-6">
        Download and print your e-tickets below. You can access your tickets at any time before your event.
      </p>

      <div className="space-y-4">
        {documents.map((doc) => {
          const imageUrl = getEventImageUrl(doc)
          const eventName = getEventName(doc)
          const eventSlug = getEventSlug(doc)

          console.log('About to render - imageUrl:', imageUrl, 'type:', typeof imageUrl)

          return (
            <div
              key={doc.id}
              className="bg-gray-700 dark:bg-gray-700 rounded-lg overflow-hidden hover:bg-gray-650 dark:hover:bg-gray-650 transition-colors"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Event Image - Square Thumbnail */}
                <div className="sm:w-32 sm:h-32 w-full h-48 flex-shrink-0">
                  {imageUrl ? (
                    eventSlug ? (
                      <a href={`/events/${eventSlug}`} className="block w-full h-full">
                        <img
                          src={imageUrl}
                          alt={eventName}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </a>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={eventName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-gray-600 dark:bg-gray-600 flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-500 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className="flex-1 p-4">
                  <h3 className="text-lg font-serif font-semibold text-white mb-2">
                    {eventSlug ? (
                      <a
                        href={`/events/${eventSlug}`}
                        className="hover:text-blue-400 dark:hover:text-blue-300 transition-colors"
                      >
                        {eventName}
                      </a>
                    ) : (
                      eventName
                    )}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-300">
                    {doc.instance?.start && (
                      <p className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formatDate(doc.instance.start)}</span>
                      </p>
                    )}
                    {doc.instance?.venue?.name && (
                      <p className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{doc.instance.venue.name}</span>
                      </p>
                    )}
                    {doc.stockItem?.name && (
                      <p className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        <span>{doc.stockItem.name}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Download Button */}
                <div className="p-4 flex items-center sm:items-start">
                  <button
                    onClick={() => handleDownload(doc.id)}
                    className="btn btn-primary bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 text-white flex items-center gap-2 w-full sm:w-auto"
                  >
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PrintHomeDocuments
